import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AVATARS } from "@/lib/identity";
import { TEST_IDS } from "@/lib/testIds";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";
import CountryPicker from "@/components/CountryPicker";

export default function ProfileSetup() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [country, setCountry] = useState(null);
  const [username, setUsername] = useState("");
  const [avatar, setAvatar] = useState(AVATARS[0].id);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!country) return toast.error("Pick where you're playing from");
    if (username.length < 3) return toast.error("Username must be at least 3 characters");
    setBusy(true);
    try {
      const { data } = await api.post("/profile/setup", { country, username, avatar });
      setUser(data);
      navigate("/home");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save profile");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-nk-bg text-nk-text">
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="flex items-center gap-3 mb-12">
          <div className="w-7 h-7 bg-nk-accent rounded-sm flex items-center justify-center">
            <span className="font-display font-bold text-nk-bg text-sm">N</span>
          </div>
          <span className="font-display text-xl font-medium tracking-tight">NKognito</span>
        </div>

        <p className="eyebrow mb-3">One last step</p>
        <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mb-3">
          Set up your profile.
        </h1>
        <p className="text-nk-mute mb-12">Three quick details, then you're in.</p>

        <section className="mb-10">
          <div className="flex items-baseline justify-between mb-4">
            <p className="font-display text-lg font-medium">Where are you playing from?</p>
            <span className="text-xs text-nk-dim font-mono">step 1 of 3</span>
          </div>
          <CountryPicker value={country} onChange={setCountry} testidPrefix="country" />
          <p className="text-xs text-nk-dim mt-3">
            Open across Africa. <span className="text-nk-text">Championship playoffs currently active in Ghana and Nigeria.</span> More countries unlock championship eligibility as the league grows.
          </p>
        </section>

        <section className="mb-10">
          <div className="flex items-baseline justify-between mb-4">
            <p className="font-display text-lg font-medium">Pick a username</p>
            <span className="text-xs text-nk-dim font-mono">step 2 of 3</span>
          </div>
          <input
            data-testid={TEST_IDS.usernameInput}
            value={username}
            onChange={(e) => setUsername(e.target.value.replace(/\s+/g, "_").slice(0, 24))}
            placeholder="e.g. dapo_quiz"
            className="w-full bg-nk-surface border border-nk-line px-4 py-3 rounded-sm focus:border-nk-accent outline-none transition-colors"
          />
          <p className="text-xs text-nk-dim mt-2">This is what other players see. 3–24 characters.</p>
        </section>

        <section className="mb-12">
          <div className="flex items-baseline justify-between mb-4">
            <p className="font-display text-lg font-medium">Pick an avatar</p>
            <span className="text-xs text-nk-dim font-mono">step 3 of 3</span>
          </div>
          <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
            {AVATARS.map((a) => {
              const Icon = a.Icon;
              const selected = avatar === a.id;
              return (
                <button
                  key={a.id}
                  data-testid={`${TEST_IDS.avatarOption}-${a.id}`}
                  onClick={() => setAvatar(a.id)}
                  className={`aspect-square flex flex-col items-center justify-center gap-2 border rounded-sm transition-all ${
                    selected ? "border-nk-accent bg-nk-accent/5" : "border-nk-line hover:border-nk-lineHi"
                  }`}
                  style={selected ? { color: a.color } : undefined}
                  title={a.label}
                >
                  <Icon size={26} weight="duotone" style={{ color: a.color }} />
                  <span className="text-[10px] text-nk-dim">{a.label}</span>
                </button>
              );
            })}
          </div>
        </section>

        <button
          data-testid={TEST_IDS.profileSaveBtn}
          onClick={submit}
          disabled={busy}
          className="inline-flex items-center gap-2 bg-nk-accent text-nk-bg font-medium px-6 py-3.5 rounded-sm hover:bg-nk-accentSoft disabled:opacity-40 transition-colors"
        >
          {busy ? "Saving..." : "Continue"}
          <ArrowRight size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
}
